import { Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import { getCurrentYear } from "../../../../components/scheduler/calendar/utils";
import { Button } from "../../../../ui/Button/Button";
import { useRef, useEffect, useState } from "react";
import { ROUTES } from "../../../../constants/routes";
import clsx from "clsx";
import classes from "./styles.module.scss";

const HEADER_HEIGHT = 52;

export const AuthPageMobile = ({
  children,
}: {
  children?: React.ReactNode;
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const formRef = useRef<HTMLDivElement>(null);
  const initialScreenRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [screenHeight, setScreenHeight] = useState(0);
  const [showBottom, setShowBottom] = useState(false);

  // Показываем initialScreen только на странице регистрации
  const isRegisterPage = location.pathname === ROUTES.LOGIN + ROUTES.REGISTER;
  const showInitialScreen = isRegisterPage;

  // Вычисляем высоту экрана через JS для корректной работы на мобильных устройствах
  useEffect(() => {
    const calculateHeight = () => {
      const height = window.innerHeight - HEADER_HEIGHT;
      setScreenHeight(height);
    };

    calculateHeight();
    window.addEventListener("resize", calculateHeight);
    window.addEventListener("orientationchange", calculateHeight);

    setTimeout(calculateHeight, 100);
    setTimeout(() => {
      setShowBottom(true)
    }, 10);
    return () => {
      window.removeEventListener("resize", calculateHeight);
      window.removeEventListener("orientationchange", calculateHeight);
    };
  }, []);

  useEffect(() => {
    if (screenHeight > 0) {
      if (containerRef.current) {
        containerRef.current.style.height = `${screenHeight}px`;
        containerRef.current.style.scrollPaddingTop = "0px";
      }
      if (initialScreenRef.current && showInitialScreen) {
        initialScreenRef.current.style.height = `${screenHeight}px`;
        initialScreenRef.current.style.minHeight = `${screenHeight}px`;
      }
      if (formRef.current) {
        if (showInitialScreen && showBottom) {
          formRef.current.style.minHeight = `${screenHeight}px`;
          formRef.current.style.height = "100%";
        } else {
          const contentHeight = formRef.current.scrollHeight;
          if (contentHeight <= screenHeight) {
            formRef.current.style.height = `${screenHeight}px`;
            formRef.current.style.minHeight = `${screenHeight}px`;
          } else {
            formRef.current.style.height = "100%";
            formRef.current.style.minHeight = `${screenHeight}px`;
          }
        }
      }
    }
  }, [screenHeight, showInitialScreen, showBottom]);

  useEffect(() => {
    if (!showInitialScreen && formRef.current && screenHeight > 0) {
      const updateHeight = () => {
        if (formRef.current) {
          const contentHeight = formRef.current.scrollHeight;
          if (contentHeight <= screenHeight) {
            formRef.current.style.height = `${screenHeight}px`;
            formRef.current.style.minHeight = `${screenHeight}px`;
          } else {
            formRef.current.style.height = "100%";
            formRef.current.style.minHeight = `${screenHeight}px`;
          }
        }
      };

      const resizeObserver = new ResizeObserver(updateHeight);
      if (formRef.current) {
        resizeObserver.observe(formRef.current);
      }

      updateHeight();

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [screenHeight, showInitialScreen, children]);

  useEffect(() => {
    if (!containerRef.current) return;

    const handleScroll = () => {
      if (!containerRef.current || !formRef.current) return;

      const formRect = formRef.current.getBoundingClientRect();

      if (!(formRect.top <= HEADER_HEIGHT && formRect.top >= 0)) {
        formRef.current.style.paddingTop = "";
      }
    };
    containerRef.current.addEventListener("scroll", handleScroll);

    handleScroll();

    return () => {
      if (containerRef.current) {
        containerRef.current.removeEventListener("scroll", handleScroll);
      }
    };
  }, [showInitialScreen]);

  const scrollToForm = () => {
    if (formRef.current && containerRef.current) {
      const container = containerRef.current;
      const targetTop = formRef.current.offsetTop;

      // Скроллим именно контейнер, а не весь viewport,
      // чтобы поведение максимально совпадало с обычным свайпом.
      container.scrollTo({
        top: targetTop,
        behavior: "smooth",
      });
    }
  };

  return (
    <div
      ref={containerRef}
      className={clsx(classes.container, {
        [classes.containerWithoutInitialScreen]: !showInitialScreen,
      })}
    >
      {showInitialScreen && (
        <div ref={initialScreenRef} className={classes.initialScreen}>
          <div className={classes.imageBackground}></div>
          <div className={classes.overlay}>
            <Typography className={classes.overlayText}>
              {(() => {
                const text = t("trainingMotivation");
                // Разделяем текст по запятым и добавляем перенос после второго элемента (после "тренируй")
                const parts = text.split(", ");
                return parts.map((part, index) => (
                  <span key={index}>
                    {part}
                    {index === 1 ? (
                      <br />
                    ) : index < parts.length - 1 ? (
                      ", "
                    ) : (
                      ""
                    )}
                  </span>
                ));
              })()}
            </Typography>
          </div>
          <Button
            buttonSize="md"
            onClick={scrollToForm}
            className={classes.joinButton}
          >
            {t("join")}
          </Button>
        </div>
      )}

      {showBottom && (
        <div
          ref={formRef}
          id="auth-form"
          className={clsx(classes.formWrapper, {
            [classes.formWrapperWithoutInitialScreen]: !showInitialScreen,
          })}
        >
          {children || <Outlet />}
          <div className={classes.copyright}>
            <Typography component="span" className={classes.copyrightText}>
              © {getCurrentYear()} YNA LLC. All rights reserved
            </Typography>
          </div>
        </div>
      )}
    </div>
  );
};
